import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import useIsMobile from '@/hooks/use-is-mobile';
import { isAndroid } from '@/lib/capacitor';
import useBackDismiss from '@/hooks/use-back-dismiss';

function AlertDialog({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  ...props
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const onOpenChange = isControlled ? controlledOnOpenChange : setInternalOpen;

  useBackDismiss({ open, onClose: () => onOpenChange(false) });

  return (
    <AlertDialogPrimitive.Root
      open={open}
      onOpenChange={onOpenChange}
      {...props}
    />
  );
}
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    ref={ref}
    {...props}
  />
));
AlertDialogOverlay.displayName = 'AlertDialogOverlay';

const DesktopAlertDialogContent = React.forwardRef(
  ({ className, ...props }, ref) => (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-xl border border-border bg-card p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          className,
        )}
        {...props}
      />
    </AlertDialogPortal>
  ),
);
DesktopAlertDialogContent.displayName = 'DesktopAlertDialogContent';

/**
 * Responsive AlertDialogContent:
 * - Desktop: centered dialog
 * - Mobile iOS: bottom drawer (native iOS pattern)
 * - Mobile Android: centered Material 3 dialog
 */
const AlertDialogContent = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const isMobile = useIsMobile();

    // While hydrating (isMobile === undefined), fall back to desktop layout.
    // Android mobile: use centered dialog (Material 3 convention).
    if (isMobile && !isAndroid()) {
      return (
        <MobileAlertDrawerContent ref={ref} className={className} {...props}>
          {children}
        </MobileAlertDrawerContent>
      );
    }

    return (
      <DesktopAlertDialogContent ref={ref} className={className} {...props}>
        {children}
      </DesktopAlertDialogContent>
    );
  },
);
AlertDialogContent.displayName = 'AlertDialogContent';

/**
 * Mobile drawer wrapper that intercepts the Radix AlertDialog open/close state
 * and renders children inside a vaul Drawer instead.
 */
const MobileAlertDrawerContent = React.forwardRef(
  ({ children, className, ...props }, ref) => {
    // Radix AlertDialog manages open state internally via context.
    // We render a hidden Radix Content (for accessibility/state) and
    // visually display a Drawer. The Drawer's open state is driven by
    // the AlertDialog's data-state attribute.
    return (
      <AlertDialogPortal>
        <AlertDialogOverlay />
        <AlertDialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto max-h-[85svh] flex-col rounded-t-2xl border border-border bg-card pb-[env(safe-area-inset-bottom)] duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
            className,
          )}
          {...props}
        >
          {/* Drawer handle */}
          <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
          {/* Match the desktop dialog's `grid gap-4`: space children evenly so the
              description doesn't sit flush against the footer buttons. */}
          <div className="flex flex-col gap-4 p-6 pt-4">{children}</div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPortal>
    );
  },
);
MobileAlertDrawerContent.displayName = 'MobileAlertDrawerContent';

const AlertDialogHeader = ({ className, ...props }) => (
  <div
    className={cn(
      'flex flex-col space-y-4 text-center sm:text-left',
      className,
    )}
    {...props}
  />
);

const AlertDialogFooter = ({ className, ...props }) => (
  <div className={cn('flex flex-row gap-3', className)} {...props} />
);

const AlertDialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn(
      'font-heading text-lg font-semibold text-foreground',
      className,
    )}
    {...props}
  />
));
AlertDialogTitle.displayName = 'AlertDialogTitle';

const AlertDialogDescription = React.forwardRef(
  ({ className, ...props }, ref) => (
    <AlertDialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  ),
);
AlertDialogDescription.displayName = 'AlertDialogDescription';

const AlertDialogAction = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(buttonVariants(), 'flex-1', className)}
    {...props}
  />
));
AlertDialogAction.displayName = 'AlertDialogAction';

const AlertDialogCancel = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(buttonVariants({ variant: 'outline' }), 'flex-1', className)}
    {...props}
  />
));
AlertDialogCancel.displayName = 'AlertDialogCancel';

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
